import { useState } from 'react';
import noticeExcelDownloadResult from '../../lib/noticeExcelDownloadResult';

type ExcelDownloadAccepted = {
  /** 파일을 받을 관리자 메일 — 서버가 큐에 등록하고 이것만 돌려준다 */
  adminEmail: string;
};

type UseExcelDownloadReturn = {
  isRequesting: boolean;
  /** 현재 필터로 반출을 **요청**한다. 파일은 메일로 온다 */
  requestExcelDownload: () => Promise<void>;
};

/**
 * 엑셀 반출 요청 (AMZUWEB-684).
 *
 * 🔴 **admin 의 엑셀은 다운로드가 아니라 메일 발송이다.**
 *
 * ```
 * admin API  →  EXCEL_DOWNLOAD_QUEUE (Bull)  →  excel-worker
 *                                                ├ 파일 생성 · S3 업로드
 *                                                └ 관리자 메일 발송
 * ```
 *
 * 응답은 `{ adminEmail }` 하나고 **파일은 오지 않는다.** 그래서 이 훅은 blob 을 만들지도,
 * 저장하지도 않는다 — 하는 일은 ① 요청 ② "〇〇로 보냈습니다" 안내 ③ 실패 처리 셋뿐이다.
 * 안내는 이미 같은 일을 하는 `noticeExcelDownloadResult` 에 맡긴다(어드민 전역 문구 통일).
 *
 * ⚠️ **이름 함정 두 개** —
 * `components/table/ExcelTable` 은 반출 도구가 **아니다**(엑셀 업로드·붙여넣기 표) ·
 * `downloadBlobFile` 도 반출용이 **아니다**(카테고리 일괄 업로드 결과 파일용).
 * 둘 중 하나를 끌어다 쓰면 이 흐름과 어긋난다.
 */
export const useExcelDownload = (params: { request: () => Promise<ExcelDownloadAccepted> }): UseExcelDownloadReturn => {
  const { request } = params;
  const [isRequesting, setIsRequesting] = useState(false);

  const requestExcelDownload = async (): Promise<void> => {
    // 연타 방지 — 반출은 워커 작업이라 중복 요청이 그대로 중복 메일이 된다
    if (isRequesting) {
      return;
    }

    setIsRequesting(true);

    try {
      const result = await request();
      noticeExcelDownloadResult(result.adminEmail);
    } catch {
      /**
       * 실패 문구는 `SuperFetchV2` 인터셉터가 이미 에러 모달로 띄운다.
       * 여기서 또 띄우면 모달이 겹치고, 삼키지 않으면 로딩이 풀리지 않는다.
       */
    } finally {
      setIsRequesting(false);
    }
  };

  return { isRequesting, requestExcelDownload };
};
